import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { put } from '@/shared/api/api-client';
import type { UpdatePatientLabRequestBody } from '@/types/scheduling-api';

/**
 * PROD-3115: `PUT /api/Patients/LabRequests/{labRequestId}/Requisitions/{requisitionId}/Status`
 * (Scrips.Patient PROD-3115).
 *
 * The lab desk status of a lab order's part at ANOTHER lab: a clinic panel
 * split across labs (for example IgA at Unilabs while the rest goes to NRL).
 * Body `{ status: 'Sample collected' | 'Requested' }`. The part's status is
 * read back from the requisitions read (its test rows), so that query family
 * is invalidated with the lab orders list.
 *
 * Same gates as `UpdatePatientLabRequest`: the order must be the caller's
 * organisation's (404 otherwise); lab desk work is open to every role.
 * 409 when the order is cancelled or the part changed meanwhile.
 */
export interface UpdateLabSplitPartStatusVariables {
  labRequestId: string;
  requisitionId: string;
  status: string;
}

export const labSplitPartStatusPath = (labRequestId: string, requisitionId: string) =>
  `/api/Patients/LabRequests/${encodeURIComponent(labRequestId)}/Requisitions/${encodeURIComponent(
    requisitionId,
  )}/Status`;

export function useUpdateLabSplitPartStatus(): UseMutationResult<
  void,
  Error,
  UpdateLabSplitPartStatusVariables
> {
  const qc = useQueryClient();
  return useMutation<void, Error, UpdateLabSplitPartStatusVariables>({
    mutationFn: ({ labRequestId, requisitionId, status }) => {
      const body: UpdatePatientLabRequestBody = { status };
      return put<void>(labSplitPartStatusPath(labRequestId, requisitionId), body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lab-requisitions'] });
      void qc.invalidateQueries({ queryKey: ['lab-orders'] });
    },
  });
}
